import { forwardRef } from 'react';
import { cn } from '../../lib/utils';

const baseInputClasses = cn(
  'w-full h-10 rounded-md text-sm outline-none transition-colors duration-150',
  'bg-white text-ink-900 placeholder:text-ink-400',
  'dark:bg-ink-900 dark:text-ink-100 dark:placeholder:text-ink-500',
  'border',
  'focus:ring-4',
  'disabled:cursor-not-allowed',
  'disabled:bg-ink-50 disabled:text-ink-500',
  'dark:disabled:bg-ink-800 dark:disabled:text-ink-500',
);

export const Input = forwardRef(function Input({
  className,
  type = 'text',
  error,
  leftIcon,
  rightIcon,
  ...props
}, ref) {
  const errorClasses = error
    ? 'border-danger focus:border-danger focus:ring-danger/10'
    : 'border-ink-200 hover:border-ink-300 focus:border-brand-500 focus:ring-brand-500/10 dark:border-ink-700 dark:hover:border-ink-600';

  if (leftIcon || rightIcon) {
    return (
      <div className="relative">
        {leftIcon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 dark:text-ink-500 pointer-events-none">
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          type={type}
          className={cn(
            baseInputClasses,
            errorClasses,
            leftIcon  ? 'pl-10' : 'pl-3.5',
            rightIcon ? 'pr-10' : 'pr-3.5',
            className,
          )}
          {...props}
        />
        {rightIcon && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 dark:text-ink-500 pointer-events-none">
            {rightIcon}
          </span>
        )}
      </div>
    );
  }

  return (
    <input
      ref={ref}
      type={type}
      className={cn(baseInputClasses, errorClasses, 'px-3.5', className)}
      {...props}
    />
  );
});

export function Label({ className, children, htmlFor, required, ...props }) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn('block text-sm font-medium text-ink-700 dark:text-ink-300 mb-1.5', className)}
      {...props}
    >
      {children}
      {required && <span className="text-danger ml-0.5">*</span>}
    </label>
  );
}

export function FieldError({ children, className }) {
  if (!children) return null;
  return <p className={cn('text-xs text-danger-dark dark:text-danger mt-1.5 font-medium', className)}>{children}</p>;
}

export function FieldHint({ children, className }) {
  if (!children) return null;
  return <p className={cn('text-xs text-ink-500 dark:text-ink-400 mt-1.5', className)}>{children}</p>;
}

export default Input;
